import React, { useState, useEffect } from 'react';
import {
  Modal,
  List,
  Typography,
  Button,
  Input,
  Space,
  Tag,
  Card,
  Avatar,
  Image,
  message,
} from 'antd';
import { UserOutlined } from '@ant-design/icons';
import {
  updateGroupMemberDetails,
  previewAddGroupMember,
  addGroupMember,
  previewRemoveGroupMember,
  removeGroupMember,
} from '../../api/group';

const { Text } = Typography;

// Helper: decode a Mongoose Buffer field (possibly serialized as a plain
// array, {type:'Buffer', data:[...]}, or already-base64) into base64.
function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

// Decode an { data, contentType } img field (data possibly a plain array,
// {type:'Buffer', data:[...]}, or already-base64 string) into a data URI.
function decodeImgToDataUri(img) {
  const data = img?.data;
  const contentType = img?.contentType;
  if (!data || !contentType) return undefined;

  let base64;
  if (Array.isArray(data)) {
    base64 = arrayBufferToBase64(data);
  } else if (data?.type === 'Buffer' && Array.isArray(data.data)) {
    base64 = arrayBufferToBase64(data.data);
  } else if (typeof data === 'string') {
    base64 = data;
  } else {
    try {
      base64 = arrayBufferToBase64(data);
    } catch {
      return undefined;
    }
  }

  return `data:${contentType};base64,${base64}`;
}

// Group.profilePhotos entries are keyed by a composed label
// "Member Name - IDNumber" (no separate id_number field is stored per
// photo), so match by suffix on the member's current id_number. If no
// group-uploaded photo exists, fall back to the linked Contestant's
// profilePhoto (attached server-side as contestantProfilePhoto, matched by
// contestant_number) when available.
function getMemberPhotoSrc(member, group) {
  if (!member?.id_number) return undefined;

  const photo = group?.profilePhotos?.length
    ? group.profilePhotos.find((p) => p.name?.endsWith(` - ${member.id_number}`))
    : undefined;

  if (photo?.img) {
    const src = decodeImgToDataUri(photo.img);
    if (src) return src;
  }

  if (member.contestantProfilePhoto?.img) {
    return decodeImgToDataUri(member.contestantProfilePhoto.img);
  }

  return undefined;
}

const GroupMembersModal = ({ visible, onClose, groupMembers, group, role, onGroupUpdated }) => {
  const [members, setMembers] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [newIdNumber, setNewIdNumber] = useState('');
  const [newMemberName, setNewMemberName] = useState('');
  const [addNameRequired, setAddNameRequired] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  // Round 1: anyone can edit
  // Round 2: only admins
  const canEdit =
    group?.round === 1 || (role === 'admin' && group?.round === 2);

  // Admins can view each member's uploaded profile photo inline.
  const canViewPhotos = role === 'admin';
  const canManageRound2Members = role === 'admin' && group?.round === 2;

  useEffect(() => {
    setMembers(groupMembers || []);
  }, [groupMembers]);

  const handleFieldChange = (index, field, value) => {
    setMembers((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const doSave = async (original, updated, merge = false) => {
    if (!group?._id) return;

    return updateGroupMemberDetails(group._id, original.id_number, {
      name: updated.name,
      id_number: updated.id_number,
      merge,
    });
  };

  const handleSaveMember = async (index) => {
    const original = groupMembers[index];
    const updated = members[index];

    if (!original || !updated) return;

    const nameChanged = original.name !== updated.name;
    const idChanged = original.id_number !== updated.id_number;

    if (!nameChanged && !idChanged) return;

    try {
      setSavingId(original.id_number);

      await doSave(original, updated, false);
      message.success('Member updated');
    } catch (err) {
      // Round 2 merge flow (existing behaviour)
      if (err.status === 409 && err.data && err.data.canMerge) {
        const conflict = err.data.conflict || {};
        let conflictName = 'another profile';
        let conflictExtra = '';

        if (conflict.contestant) {
          const c = conflict.contestant;
          conflictName = `${c.first_name} ${c.last_name}`.trim();
          if (c.contestant_number != null) {
            conflictExtra = ` (contestant #${c.contestant_number})`;
          }
        } else if (conflict.groupMember) {
          const gm = conflict.groupMember;
          conflictName = gm.name || 'another group member';
          if (gm.contestant_number != null) {
            conflictExtra = ` (contestant #${gm.contestant_number})`;
          } else if (gm.group_name) {
            conflictExtra = ` in group "${gm.group_name}"`;
          }
        }

        Modal.confirm({
          title: 'ID number already in use',
          content: (
            <>
              <p>
                This ID number is already used by{' '}
                <strong>
                  {conflictName}
                  {conflictExtra}
                </strong>
                .
              </p>
              <p>
                If you merge, this member will share the same contestant number
                and their invoices and applications will be combined.
              </p>
            </>
          ),
          okText: 'Merge',
          cancelText: 'Cancel',
          onOk: async () => {
            try {
              setSavingId(original.id_number);
              await doSave(original, updated, true);
              message.success('Member merged and updated');
            } catch (mergeErr) {
              console.error(mergeErr);
              message.error(mergeErr.message || 'Failed to merge');
              setMembers(groupMembers);
            } finally {
              setSavingId(null);
            }
          },
          onCancel: () => {
            setMembers(groupMembers);
          },
        });
      }
      // Round 1 conflict or generic "same protections as create group"
      else if (err.status === 409 && err.data && err.data.conflicts) {
        const conflicts = err.data.conflicts || [];

        Modal.error({
          title: 'ID number already in use',
          content: (
            <div>
              <p>
                This ID number is already used by a different contestant in
                another group:
              </p>
              <ul>
                {conflicts.map((c, idx) => (
                  <li key={idx}>
                    ID <strong>{c.id_number}</strong> is used by{' '}
                    <strong>{c.existingMemberName || 'another contestant'}</strong>
                    {c.group_name ? ` in group "${c.group_name}"` : ''}
                    {c.newMemberName ? ` (you entered: ${c.newMemberName})` : ''}
                  </li>
                ))}
              </ul>
              <p>
                If this is the same contestant, please make sure the name you
                enter is exactly the same as the existing one and then try
                again.
              </p>
            </div>
          ),
        });

        // Reset edits back to original values
        setMembers(groupMembers);
      } else {
        console.error(err);
        message.error(err.message || 'Failed to update member');
        setMembers(groupMembers);
      }
    } finally {
      setSavingId(null);
    }
  };

  const applyUpdatedGroup = (updatedGroup) => {
    if (updatedGroup?.group) {
      setMembers(updatedGroup.group);
      onGroupUpdated?.(updatedGroup);
    }
  };

  const handleRemoveMember = async (member) => {
    try {
      setSavingId(member.id_number);
      const preview = await previewRemoveGroupMember(group._id, member.id_number);
      Modal.confirm({
        title: `Remove ${preview.memberName} from ${preview.groupName}?`,
        width: 620,
        content: (
          <div>
            <p><strong>Applications to delete:</strong> {preview.applications?.length || 0}</p>
            {preview.applications?.map((application) => (
              <div key={application._id}>
                {application.eventName || 'Unnamed event'}{application.eventStyle ? ` - ${application.eventStyle}` : ''} ({application._id})
              </div>
            ))}
            <p style={{ marginTop: 12 }}><strong>Invoices to update (not deleted):</strong></p>
            {preview.invoices?.length ? preview.invoices.map((invoice) => (
              <div key={invoice._id}>
                {invoice.invoiceNumber || invoice._id} - {invoice.paid ? 'Paid' : 'Not paid'}
                {invoice.receiptCount ? ` (${invoice.receiptCount} receipt(s))` : ''}
              </div>
            )) : <div>No affected invoices.</div>}
            {preview.warnings?.map((warning) => <p key={warning} style={{ color: '#b45309' }}>{warning}</p>)}
          </div>
        ),
        okText: 'Remove Member',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        onOk: async () => {
          const result = await removeGroupMember(group._id, member.id_number);
          applyUpdatedGroup(result.group);
          message.success('Member removed');
        },
      });
    } catch (error) {
      message.error(error.message || 'Unable to load removal details');
    } finally {
      setSavingId(null);
    }
  };

  const handlePreviewAddMember = async () => {
    const idNumber = newIdNumber.trim();
    if (!idNumber) return;
    try {
      setAddLoading(true);
      const preview = await previewAddGroupMember(group._id, idNumber, newMemberName);
      Modal.confirm({
        title: `Add ${preview.memberName} to ${preview.groupName}?`,
        width: 620,
        content: (
          <div>
            <p><strong>ID number:</strong> {preview.idNumber}</p>
            <p><strong>Invoice:</strong> {preview.invoice?.willCreate ? 'A new invoice will be created' : (preview.invoice?.invoiceNumber || 'Existing invoice will be updated')}</p>
            <p><strong>Applications to create:</strong> {preview.applications?.length || 0}</p>
            {preview.applications?.map((application) => (
              <div key={application._id}>
                {application.eventName || 'Unnamed event'}{application.eventStyle ? ` - ${application.eventStyle}` : ''} ({application._id})
              </div>
            ))}
            {preview.existingApplications?.length ? <p>Existing matching applications will be linked without duplication.</p> : null}
          </div>
        ),
        okText: 'Add Member',
        cancelText: 'Cancel',
        onOk: async () => {
          const result = await addGroupMember(group._id, idNumber, newMemberName);
          applyUpdatedGroup(result.group);
          setNewIdNumber('');
          setNewMemberName('');
          setAddNameRequired(false);
          setAddModalVisible(false);
          message.success('Member added');
        },
      });
    } catch (error) {
      if (error.status === 422 && error.data?.requiresName) {
        setAddNameRequired(true);
        message.info('No matching contestant or group member was found. Enter the new member\'s name to continue.');
      } else {
        message.error(error.message || 'Unable to load member details');
      }
    } finally {
      setAddLoading(false);
    }
  };

  return (
    <Modal
      title={
        <>
          Group Members
          {typeof group?.round === 'number' && (
            <Tag style={{ marginLeft: 8 }}>Round {group.round}</Tag>
          )}
        </>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        canManageRound2Members && (
          <Button key="add" type="primary" onClick={() => setAddModalVisible(true)}>
            Add Member
          </Button>
        ),
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      <Image.PreviewGroup>
      <List
        locale={{ emptyText: 'No Members' }}
        dataSource={members}
        renderItem={(member, index) => (
          <List.Item key={member._id || index} style={{ padding: '8px 0', border: 'none' }}>
            <Card
              size="small"
              style={{ width: '100%', borderRadius: 8 }}
              bodyStyle={{ padding: '12px 16px' }}
            >
            <div style={{ display: 'flex', gap: 12 }}>
            {canViewPhotos && (
              <div style={{ textAlign: 'center', flexShrink: 0 }}>
                {getMemberPhotoSrc(member, group) ? (
                  <Image
                    src={getMemberPhotoSrc(member, group)}
                    width={48}
                    height={48}
                    style={{ objectFit: 'cover', borderRadius: 6 }}
                  />
                ) : (
                  <>
                    <Avatar shape="square" size={48} icon={<UserOutlined />} />
                    <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                      No photo
                    </div>
                  </>
                )}
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
            {canEdit ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                {member.contestant_number != null && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Contestant No.:</Text>
                    <Tag color="blue" style={{ margin: 0 }}>{member.contestant_number}</Tag>
                  </div>
                )}

                <div>
                  <Text strong>Name</Text>
                  <Input
                    value={member.name}
                    onChange={(e) =>
                      handleFieldChange(index, 'name', e.target.value)
                    }
                    style={{ marginTop: 4 }}
                  />
                </div>

                <div>
                  <Text strong>ID Number</Text>
                  <Input
                    value={member.id_number}
                    onChange={(e) =>
                      handleFieldChange(index, 'id_number', e.target.value)
                    }
                    style={{ marginTop: 4 }}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    No ID or Passport? Enter Date of Birth (e.g., 2012/05/19 — YYYY/MM/DD)
                  </Text>
                </div>

                <Button
                  type="primary"
                  onClick={() => handleSaveMember(index)}
                  loading={savingId === groupMembers[index]?.id_number}
                  disabled={
                    !member.name ||
                    !member.id_number ||
                    (member.name === groupMembers[index]?.name &&
                      member.id_number ===
                        groupMembers[index]?.id_number)
                  }
                >
                  Save Changes
                </Button>
                {canManageRound2Members && (
                  <Button
                    danger
                    onClick={() => handleRemoveMember(member)}
                    loading={savingId === member.id_number}
                  >
                    Remove Member
                  </Button>
                )}
              </Space>
            ) : (
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Text strong>{member.name}</Text>
                  {member.contestant_number != null && (
                    <Tag color="blue" style={{ margin: 0 }}>
                      #{member.contestant_number}
                    </Tag>
                  )}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>{member.id_number}</Text>
              </div>
            )}
            </div>
            </div>
            </Card>
          </List.Item>
        )}
      />
      </Image.PreviewGroup>
      {!canEdit && (
        <Text type="secondary">
          Only admins can edit members for this group.
        </Text>
      )}
      <Modal
        title="Add Group Member"
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          setNewIdNumber('');
          setNewMemberName('');
          setAddNameRequired(false);
        }}
        onOk={handlePreviewAddMember}
        okText="Review"
        confirmLoading={addLoading}
        okButtonProps={{ disabled: !newIdNumber.trim() || (addNameRequired && !newMemberName.trim()) }}
      >
        <Text>Enter the member&apos;s ID number or passport number.</Text>
        <Input
          autoFocus
          value={newIdNumber}
          onChange={(event) => setNewIdNumber(event.target.value)}
          onPressEnter={handlePreviewAddMember}
          style={{ marginTop: 8 }}
        />
        {addNameRequired && (
          <>
            <Text strong style={{ display: 'block', marginTop: 12 }}>Member name</Text>
            <Input
              autoFocus
              value={newMemberName}
              onChange={(event) => setNewMemberName(event.target.value)}
              onPressEnter={handlePreviewAddMember}
              placeholder="Enter the new group member's name"
              style={{ marginTop: 8 }}
            />
          </>
        )}
      </Modal>
    </Modal>
  );
};

export default GroupMembersModal;
